import React, { useState, useEffect } from 'react';
import type { BusinessCard } from '../lib/types';

interface CardFormProps {
  mode: 'create' | 'edit';
  initialData?: BusinessCard;
  onSubmit: (data: Omit<BusinessCard, 'localId' | 'arkivKey' | 'walletAddress' | 'createdAt' | 'updatedAt'>, expiresAt?: Date) => void;
  onFormChange?: (data: Partial<BusinessCard>) => void;
  onDelete?: () => void;
  isLoading?: boolean;
}

export function CardForm({ mode, initialData, onSubmit, onFormChange, onDelete, isLoading }: CardFormProps) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    title: initialData?.title || '',
    company: initialData?.company || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    website: initialData?.website || '',
    bio: initialData?.bio || '',
    social: {
      linkedin: initialData?.social?.linkedin || '',
      twitter: initialData?.social?.twitter || '',
      github: initialData?.social?.github || '',
    },
    location: initialData?.location || {
      address: '',
      coordinates: { lat: 0, lng: 0 },
    },
  });
  const [expiresAt, setExpiresAt] = useState<Date | null>(
    initialData?.expiresAt ? new Date(initialData.expiresAt) : null
  );

  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationSuggestions, setLocationSuggestions] = useState<Array<{ label: string; lat: number; lng: number }>>([]);
  const [expiryChoice, setExpiryChoice] = useState(() => {
    if (!expiresAt) return 'never';
    const diffDays = Math.round((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return '1d';
    if (diffDays <= 8) return '1w';
    if (diffDays >= 360) return '1y';
    return 'custom';
  });
  const [coordsInput, setCoordsInput] = useState({
    lat: initialData?.location.coordinates.lat?.toString() ?? '',
    lng: initialData?.location.coordinates.lng?.toString() ?? '',
  });

  useEffect(() => {
    if (onFormChange) {
      onFormChange({ ...formData, expiresAt: expiresAt?.toISOString() });
    }
  }, [formData, expiresAt, onFormChange]);

  // Address autocomplete to coordinates
  useEffect(() => {
    const query = formData.location.address.trim();
    if (query.length < 3) {
      setLocationSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`, {
          signal: controller.signal,
          headers: { 'Accept-Language': 'en' },
        });
        const data = await res.json();
        const results = Array.isArray(data)
          ? data.map((item: any) => ({
              label: item.display_name as string,
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon),
            }))
          : [];
        setLocationSuggestions(results);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setLocationSuggestions([]);
        }
      }
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [formData.location.address]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('social.')) {
      const socialKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        social: { ...prev.social, [socialKey]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const expiryOptions = [
    { value: 'never', label: 'Never' },
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1y', label: '1 Year' },
  ];

  const applyExpiryChoice = (value: string) => {
    setExpiryChoice(value);
    if (value === 'never') {
      setExpiresAt(null);
      return;
    }
    const now = Date.now();
    let ms = 0;
    if (value === '1d') ms = 1000 * 60 * 60 * 24;
    if (value === '1w') ms = 1000 * 60 * 60 * 24 * 7;
    if (value === '1y') ms = 1000 * 60 * 60 * 24 * 365;
    setExpiresAt(new Date(now + ms));
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, address: value },
    }));
  };

  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCoordsInput(prev => ({ ...prev, [name]: value }));
    const nextVal = parseFloat(value);
    if (Number.isFinite(nextVal)) {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          coordinates: { ...prev.location.coordinates, [name]: nextVal },
        },
      }));
    }
  };

  const selectSuggestion = (suggestion: { label: string; lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        address: suggestion.label,
        coordinates: { lat: suggestion.lat, lng: suggestion.lng },
      },
    }));
    setCoordsInput({ lat: suggestion.lat.toString(), lng: suggestion.lng.toString() });
    setLocationSuggestions([]);
  };

  const getCurrentLocation = async () => {
    setGettingLocation(true);
    setLocationError(null);
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported.');
      setGettingLocation(false);
      return;
    }
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });
      const { latitude, longitude, accuracy } = position.coords;
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
      const data = await response.json();
      setCoordsInput({ lat: latitude.toString(), lng: longitude.toString() });
      setFormData(prev => ({
        ...prev,
        location: {
          address: data.display_name || `${latitude}, ${longitude}`,
          coordinates: { lat: latitude, lng: longitude },
          accuracy,
        },
      }));
    } catch (error: any) {
      setLocationError(`Error: ${error.message}`);
    } finally {
      setGettingLocation(false);
    }
  };

  const forwardGeocode = async () => {
    const query = formData.location.address.trim();
    if (query.length < 3) {
      setLocationError('Address is too short to search.');
      return;
    }
    setGettingLocation(true);
    setLocationError(null);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const data = await res.json();
      const first = Array.isArray(data) && data[0];
      if (!first) {
        setLocationError('No results found for that address.');
        setGettingLocation(false);
        return;
      }
      const lat = parseFloat(first.lat);
      const lng = parseFloat(first.lon);
      selectSuggestion({ label: first.display_name, lat, lng });
    } catch (error: any) {
      setLocationError(`Lookup failed: ${error.message}`);
    } finally {
      setGettingLocation(false);
    }
  };

  const reverseGeocode = async () => {
    setLocationError(null);
    const lat = parseFloat(coordsInput.lat);
    const lng = parseFloat(coordsInput.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setLocationError('Enter valid latitude and longitude to reverse lookup.');
      return;
    }
    setGettingLocation(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await response.json();
      const address = data.display_name || `${lat}, ${lng}`;
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          address,
          coordinates: { lat, lng },
        },
      }));
    } catch (error: any) {
      setLocationError(`Reverse lookup failed: ${error.message}`);
    } finally {
      setGettingLocation(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.bio || !formData.location.address) {
      alert('Name, Bio, and Location are required.');
      return;
    }
    onSubmit(formData, expiresAt || undefined);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-6 md:col-span-1">
          <h3 className="text-xl font-semibold">Core Info</h3>
          <InputField id="name" name="name" label="Name *" value={formData.name} onChange={handleInputChange} required disabled={isLoading} />
          <InputField id="title" name="title" label="Title" value={formData.title} onChange={handleInputChange} disabled={isLoading} />
          <InputField id="company" name="company" label="Company" value={formData.company} onChange={handleInputChange} disabled={isLoading} />
          <div>
            <label htmlFor="bio" className="label">Bio *</label>
            <textarea id="bio" name="bio" value={formData.bio} onChange={handleInputChange} className="textarea mt-1" rows={4} required disabled={isLoading} />
          </div>
        </div>

        <div className="space-y-6 md:col-span-1">
          <h3 className="text-xl font-semibold">Contact</h3>
          <InputField id="email" name="email" type="email" label="Email" value={formData.email} onChange={handleInputChange} disabled={isLoading} />
          <InputField id="phone" name="phone" type="tel" label="Phone" value={formData.phone} onChange={handleInputChange} disabled={isLoading} />
          <InputField id="website" name="website" type="url" label="Website" value={formData.website} onChange={handleInputChange} disabled={isLoading} />
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-xl font-semibold">Location</h3>
        <div className="flex flex-col gap-3">
          <div className="flex gap-2 relative">
            <input type="text" id="address" name="address" value={formData.location.address} onChange={handleLocationChange} className="input flex-1" required disabled={isLoading || gettingLocation} placeholder="123 Street, City, Country" />
            <button type="button" onClick={getCurrentLocation} disabled={gettingLocation || isLoading} className="btn btn-secondary">
              {gettingLocation ? 'Getting...' : '📍 Current'}
            </button>
            {locationSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-2 rounded-lg border border-slate-200 bg-white shadow-lg">
                <ul className="max-h-48 overflow-auto divide-y divide-slate-100">
                  {locationSuggestions.map((item, idx) => (
                    <li
                      key={`${item.lat}-${item.lng}-${idx}`}
                      className="cursor-pointer px-3 py-2 text-sm hover:bg-slate-50"
                      onClick={() => selectSuggestion(item)}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
            <input
              type="text"
              name="lat"
              value={coordsInput.lat}
              onChange={handleCoordinateChange}
              placeholder="Latitude"
              className="input"
              disabled={isLoading || gettingLocation}
            />
            <input
              type="text"
              name="lng"
              value={coordsInput.lng}
              onChange={handleCoordinateChange}
              placeholder="Longitude"
              className="input"
              disabled={isLoading || gettingLocation}
            />
            <button
              type="button"
              onClick={reverseGeocode}
              disabled={gettingLocation || isLoading}
              className="btn btn-outline"
            >
              Reverse lookup
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={forwardGeocode}
              disabled={gettingLocation || isLoading}
              className="btn btn-secondary btn-sm"
            >
              Lookup address → coordinates
            </button>
            <button
              type="button"
              onClick={() => setLocationSuggestions([])}
              className="btn btn-outline btn-sm"
            >
              Clear suggestions
            </button>
          </div>
          {locationError && <p className="text-sm text-red-500">{locationError}</p>}
          {formData.location.accuracy !== undefined && (
            <p className="text-xs text-slate-500">Accuracy: ±{Math.round(formData.location.accuracy)}m</p>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-xl font-semibold">Social Media</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <InputField id="linkedin" name="social.linkedin" label="LinkedIn" value={formData.social.linkedin} onChange={handleInputChange} disabled={isLoading} />
          <InputField id="twitter" name="social.twitter" label="Twitter/X" value={formData.social.twitter} onChange={handleInputChange} disabled={isLoading} />
          <InputField id="github" name="social.github" label="GitHub" value={formData.social.github} onChange={handleInputChange} disabled={isLoading} />
        </div>
      </div>
      
      <div className="space-y-6">
        <h3 className="text-xl font-semibold">Expiration</h3>
        <div className="space-y-2">
          <label htmlFor="expiresAt" className="label">Expires At</label>
          <select
            id="expiresAt"
            value={expiryChoice}
            onChange={(e) => applyExpiryChoice(e.target.value)}
            className="input"
            disabled={isLoading}
          >
            {expiryOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="text-sm text-slate-500">
            {expiresAt ? `Will expire on ${expiresAt.toLocaleString()}` : 'Card will not expire.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-4 pt-4 md:flex-row md:justify-end">
        {mode === 'edit' && onDelete && (
          <button type="button" onClick={onDelete} disabled={isLoading} className="btn btn-danger md:mr-auto">
            Delete Card
          </button>
        )}
        <button type="submit" disabled={isLoading} className="btn btn-primary btn-lg w-full md:w-auto">
          {isLoading ? 'Saving...' : (mode === 'create' ? 'Create Card' : 'Save Changes')}
        </button>
      </div>
    </form>
  );
}

// Helper component for input fields
interface InputFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  disabled?: boolean;
}

function InputField({ id, name, label, value, onChange, type = 'text', required = false, disabled = false }: InputFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      <input type={type} id={id} name={name} value={value} onChange={onChange} className="input mt-1" required={required} disabled={disabled} />
    </div>
  );
}
